import { Inject, Injectable, Optional } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { catchError, delay, finalize, map } from 'rxjs/operators';

import { AdminDashboardReportsPort } from '../../../app/ports/reports/admin-dashboard-reports.port';
import { AppEventsService } from '../../events/products-events.service';
import { CoreUIToastDirection, CoreUIToastV2Service } from '../../services/toast.service';
import { ReportSdk } from 'lib-klab-sdks';
import { EnvMicroReport } from '../../config/env.micro.report';
import { REPORT_ENV } from '../../config/micros-env.tokens';

import { AdminUsageHistoryRequest } from '../../../domain/models/reports/request/admin-usage-history.request';
import { PeriodRankingResponseDomain } from '../../../domain/models/reports/period-ranking-response.model';
import { ActivityHistoryResponseDomain } from '../../../domain/models/reports/activity-history-response.model';
import { EmployeeSummaryResponseDomain } from '../../../domain/models/reports/employee-summary-response.model';
import { ActivityBreakdownResponseDomain } from '../../../domain/models/reports/activity-breakdown-response.model';
import {
    mapSdkPeriodRankingResponseToDomain,
    mapSdkActivityHistoryResponseToDomain,
    mapSdkEmployeeSummaryResponseToDomain,
    mapSdkActivityBreakdownResponseToDomain,
} from '../../mappers/reports/admin-dashboard-reports.mapper';
import { adminDashboardReportsSdkMock } from '../../mocks/report/admin-dashboard-reports.mock';

type AdminDashboardReportsMockAction =
    | 'getEmployeeSummary'
    | 'getActivityBreakdown'
    | 'getUsageWebsTop'
    | 'getUsageAppsTop'
    | 'getUsageWebsHistory'
    | 'getUsageAppsHistory'
    | 'getProductivityDepartments'
    | 'getProductivityLocations'
    | 'getProductivityEmployeesTop'
    | 'getProductivityEmployeesBottom'
    | 'getProductivityEmployeesTopHistory'
    | 'getProductivityEmployeesBottomHistory';

@Injectable({ providedIn: 'root' })
export class AdminDashboardReportsPortAdapter implements AdminDashboardReportsPort {
    constructor(
        private adminDashboardService: ReportSdk.AdminDashboardService,
        private appEvents: AppEventsService,
        @Optional() private toast: CoreUIToastV2Service,
        @Inject(REPORT_ENV) private env: EnvMicroReport,
    ) { }

    getEmployeeSummary(): Observable<EmployeeSummaryResponseDomain> {
        this.appEvents.loaderVisiblePublish(true, 'report-admin-employee-summary');
        const source$ = this.shouldMock('getEmployeeSummary')
            ? of(adminDashboardReportsSdkMock.getEmployeeSummary()).pipe(delay(360))
            : this.adminDashboardService.getEmployeeSummary();
        return source$.pipe(
            map((resp) => mapSdkEmployeeSummaryResponseToDomain(resp as any)),
            catchError((err) => {
                this.showErrorToast(err);
                return throwError(() => err);
            }),
            finalize(() => this.appEvents.loaderVisiblePublish(false, 'report-admin-employee-summary')),
        );
    }

    getActivityBreakdown(): Observable<ActivityBreakdownResponseDomain> {
        this.appEvents.loaderVisiblePublish(true, 'report-admin-activity-breakdown');
        const source$ = this.shouldMock('getActivityBreakdown')
            ? of(adminDashboardReportsSdkMock.getActivityBreakdown()).pipe(delay(360))
            : this.adminDashboardService.getActivityBreakdown();
        return source$.pipe(
            map((resp) => mapSdkActivityBreakdownResponseToDomain(resp as any)),
            catchError((err) => {
                this.showErrorToast(err);
                return throwError(() => err);
            }),
            finalize(() => this.appEvents.loaderVisiblePublish(false, 'report-admin-activity-breakdown')),
        );
    }

    getUsageWebsTop(limit?: number): Observable<PeriodRankingResponseDomain> {
        this.appEvents.loaderVisiblePublish(true, 'report-admin-usage-webs-top');
        const source$ = this.shouldMock('getUsageWebsTop')
            ? of(adminDashboardReportsSdkMock.getUsageWebsTop(limit)).pipe(delay(360))
            : this.adminDashboardService.getUsageWebsTop(limit ?? undefined);
        return source$.pipe(
            map((resp) => mapSdkPeriodRankingResponseToDomain(resp as any)),
            catchError((err) => {
                this.showErrorToast(err);
                return throwError(() => err);
            }),
            finalize(() => this.appEvents.loaderVisiblePublish(false, 'report-admin-usage-webs-top')),
        );
    }

    getUsageAppsTop(limit?: number): Observable<PeriodRankingResponseDomain> {
        this.appEvents.loaderVisiblePublish(true, 'report-admin-usage-apps-top');
        const source$ = this.shouldMock('getUsageAppsTop')
            ? of(adminDashboardReportsSdkMock.getUsageAppsTop(limit)).pipe(delay(360))
            : this.adminDashboardService.getUsageAppsTop(limit ?? undefined);
        return source$.pipe(
            map((resp) => mapSdkPeriodRankingResponseToDomain(resp as any)),
            catchError((err) => {
                this.showErrorToast(err);
                return throwError(() => err);
            }),
            finalize(() => this.appEvents.loaderVisiblePublish(false, 'report-admin-usage-apps-top')),
        );
    }

    getUsageWebsHistory(params?: AdminUsageHistoryRequest): Observable<ActivityHistoryResponseDomain> {
        this.appEvents.loaderVisiblePublish(true, 'report-admin-usage-webs-history');
        const source$ = this.shouldMock('getUsageWebsHistory')
            ? of(adminDashboardReportsSdkMock.getUsageWebsHistory()).pipe(delay(360))
            : this.adminDashboardService.getUsageWebsHistory(
                params?.page, params?.size, params?.locationId,
                params?.departmentId, params?.employeeId, params?.webs
            );
        return source$.pipe(
            map((resp) => mapSdkActivityHistoryResponseToDomain(resp as any)),
            catchError((err) => {
                this.showErrorToast(err);
                return throwError(() => err);
            }),
            finalize(() => this.appEvents.loaderVisiblePublish(false, 'report-admin-usage-webs-history')),
        );
    }

    getUsageAppsHistory(params?: AdminUsageHistoryRequest): Observable<ActivityHistoryResponseDomain> {
        this.appEvents.loaderVisiblePublish(true, 'report-admin-usage-apps-history');
        const source$ = this.shouldMock('getUsageAppsHistory')
            ? of(adminDashboardReportsSdkMock.getUsageAppsHistory()).pipe(delay(360))
            : this.adminDashboardService.getUsageAppsHistory(
                params?.page, params?.size, params?.locationId,
                params?.departmentId, params?.employeeId, params?.apps
            );
        return source$.pipe(
            map((resp) => mapSdkActivityHistoryResponseToDomain(resp as any)),
            catchError((err) => {
                this.showErrorToast(err);
                return throwError(() => err);
            }),
            finalize(() => this.appEvents.loaderVisiblePublish(false, 'report-admin-usage-apps-history')),
        );
    }

    getProductivityDepartments(): Observable<PeriodRankingResponseDomain> {
        this.appEvents.loaderVisiblePublish(true, 'report-admin-productivity-departments');
        const source$ = this.shouldMock('getProductivityDepartments')
            ? of(adminDashboardReportsSdkMock.getProductivityDepartments()).pipe(delay(360))
            : this.adminDashboardService.getProductivityDepartments();
        return source$.pipe(
            map((resp) => mapSdkPeriodRankingResponseToDomain(resp as any)),
            catchError((err) => {
                this.showErrorToast(err);
                return throwError(() => err);
            }),
            finalize(() => this.appEvents.loaderVisiblePublish(false, 'report-admin-productivity-departments')),
        );
    }

    getProductivityLocations(): Observable<PeriodRankingResponseDomain> {
        this.appEvents.loaderVisiblePublish(true, 'report-admin-productivity-locations');
        const source$ = this.shouldMock('getProductivityLocations')
            ? of(adminDashboardReportsSdkMock.getProductivityLocations()).pipe(delay(360))
            : this.adminDashboardService.getProductivityLocations();
        return source$.pipe(
            map((resp) => mapSdkPeriodRankingResponseToDomain(resp as any)),
            catchError((err) => {
                this.showErrorToast(err);
                return throwError(() => err);
            }),
            finalize(() => this.appEvents.loaderVisiblePublish(false, 'report-admin-productivity-locations')),
        );
    }

    getProductivityEmployeesTop(limit?: number): Observable<PeriodRankingResponseDomain> {
        this.appEvents.loaderVisiblePublish(true, 'report-admin-productivity-employees-top');
        const source$ = this.shouldMock('getProductivityEmployeesTop')
            ? of(adminDashboardReportsSdkMock.getProductivityEmployeesTop()).pipe(delay(360))
            : this.adminDashboardService.getProductivityEmployeesTop(limit ?? undefined);
        return source$.pipe(
            map((resp) => mapSdkPeriodRankingResponseToDomain(resp as any)),
            catchError((err) => {
                this.showErrorToast(err);
                return throwError(() => err);
            }),
            finalize(() => this.appEvents.loaderVisiblePublish(false, 'report-admin-productivity-employees-top')),
        );
    }

    getProductivityEmployeesBottom(limit?: number): Observable<PeriodRankingResponseDomain> {
        this.appEvents.loaderVisiblePublish(true, 'report-admin-productivity-employees-bottom');
        const source$ = this.shouldMock('getProductivityEmployeesBottom')
            ? of(adminDashboardReportsSdkMock.getProductivityEmployeesBottom()).pipe(delay(360))
            : this.adminDashboardService.getProductivityEmployeesBottom(limit ?? undefined);
        return source$.pipe(
            map((resp) => mapSdkPeriodRankingResponseToDomain(resp as any)),
            catchError((err) => {
                this.showErrorToast(err);
                return throwError(() => err);
            }),
            finalize(() => this.appEvents.loaderVisiblePublish(false, 'report-admin-productivity-employees-bottom')),
        );
    }

    getProductivityEmployeesTopHistory(params: {
        employeesId?: number[];
        context?: string;
        search?: string;
        page?: number;
        size?: number;
    }): Observable<ActivityHistoryResponseDomain> {
        this.appEvents.loaderVisiblePublish(true, 'report-admin-productivity-employees-top-history');
        const source$ = this.shouldMock('getProductivityEmployeesTopHistory')
            ? of(adminDashboardReportsSdkMock.getProductivityEmployeesTopHistory()).pipe(delay(360))
            : this.adminDashboardService.getProductivityEmployeesTopHistory(
                params.page, params.size, params.employeesId, params.context, params.search
            );
        return source$.pipe(
            map((resp) => mapSdkActivityHistoryResponseToDomain(resp as any)),
            catchError((err) => {
                this.showErrorToast(err);
                return throwError(() => err);
            }),
            finalize(() => this.appEvents.loaderVisiblePublish(false, 'report-admin-productivity-employees-top-history')),
        );
    }

    getProductivityEmployeesBottomHistory(params: {
        employeesId?: number[];
        context?: string;
        search?: string;
        page?: number;
        size?: number;
    }): Observable<ActivityHistoryResponseDomain> {
        this.appEvents.loaderVisiblePublish(true, 'report-admin-productivity-employees-bottom-history');
        const source$ = this.shouldMock('getProductivityEmployeesBottomHistory')
            ? of(adminDashboardReportsSdkMock.getProductivityEmployeesBottomHistory()).pipe(delay(360))
            : this.adminDashboardService.getProductivityEmployeesBottomHistory(
                params.page, params.size, params.employeesId, params.context, params.search
            );
        return source$.pipe(
            map((resp) => mapSdkActivityHistoryResponseToDomain(resp as any)),
            catchError((err) => {
                this.showErrorToast(err);
                return throwError(() => err);
            }),
            finalize(() => this.appEvents.loaderVisiblePublish(false, 'report-admin-productivity-employees-bottom-history')),
        );
    }

    private shouldMock(action: AdminDashboardReportsMockAction): boolean {
        return Boolean(this.env?.useMocks || this.env?.mock?.adminDashboard?.[action]);
    }

    private showErrorToast(err: unknown): void {
        if (!this.toast) return;
        const status = (err as { status?: number })?.status;
        const mapMsg: Record<number, string> = {
            400: 'Solicitud inválida',
            401: 'No autorizado',
            404: 'No encontrado',
            429: 'Demasiadas solicitudes',
            500: 'Error interno del servidor',
        };
        this.toast.show({
            data: {
                title: 'Error',
                msg: mapMsg[status ?? 0] || 'Error inesperado',
                direction: CoreUIToastDirection.topCenter,
                autoClose: true,
            },
        });
    }
}
